import { GlassType } from '../types/glassTypes';
import { parseCSVToGlassTypes, fetchAndParseCSV } from './csvUtils';
import { scanGlassPricingDirectories, loadGlassTypesFromDirectory } from './directoryScanner';
import { v4 as uuidv4 } from 'uuid';

// Define the standard glass type categories
export const GLASS_TYPE_CATEGORIES = [
  'Mirror',
  'Monolithic',
  'Laminated',
  'Wire',
  'Fire Rated',
  'Pattern',
  'Spandrel',
  'Specialty',
  'IG Commercial',
  'IG Residential'
];

// Map file names to glass type categories
const FILE_TO_CATEGORY_MAP: Record<string, string> = {
  'Mirrors.csv': 'Mirror',
  'Monolithic.csv': 'Monolithic',
  'Laminate.csv': 'Laminated',
  'Wire.csv': 'Wire',
  'Fire_Rated.csv': 'Fire Rated',
  'Pattern.csv': 'Pattern',
  'Spandrel.csv': 'Spandrel',
  'Specialty.csv': 'Specialty',
  'IG Commercial.csv': 'IG Commercial',
  'IG Residential.csv': 'IG Residential'
};

/**
 * Loads all glass types from the CSV files in the /public/glass_pricing/ folder
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Promise resolving to an array of GlassType objects
 */
export const loadAllGlassTypes = async (source: string): Promise<GlassType[]> => {
  try {
    console.log(`Loading all glass types for source: ${source}`);
    
    // Get the list of all CSV files in the glass_pricing folder
    const files = Object.keys(FILE_TO_CATEGORY_MAP);
    
    // Load each file and combine the results
    const allTypesPromises = files.map(async (file) => {
      try {
        const url = `/glass_pricing/${file}`;
        console.log(`Loading glass types from ${url}`);
        const types = await fetchAndParseCSV(url, source);
        
        // Ensure the type field is set correctly based on the file name
        return types.map(type => ({
          ...type,
          type: FILE_TO_CATEGORY_MAP[file] || type.type
        }));
      } catch (error) {
        console.error(`Error loading glass types from ${file}:`, error);
        return [];
      }
    });
    
    // Wait for all files to be loaded and combine the results
    const allTypesArrays = await Promise.all(allTypesPromises);
    const allTypes = allTypesArrays.flat();
    
    console.log(`Loaded ${allTypes.length} glass types from all files`);
    return allTypes;
  } catch (error) {
    console.error('Error loading all glass types:', error);
    return [];
  }
};

/**
 * Loads glass types for a specific category from the corresponding CSV file
 * @param category Glass type category (e.g., 'Mirror', 'Monolithic')
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Promise resolving to an array of GlassType objects
 */
export const loadGlassTypesByCategory = async (category: string, source: string): Promise<GlassType[]> => {
  try {
    // Find the file name for the given category
    const fileName = Object.entries(FILE_TO_CATEGORY_MAP)
      .find(([_, cat]) => cat.toLowerCase() === category.toLowerCase())?.[0];
    
    if (!fileName) {
      console.error(`No file found for category: ${category}`);
      return [];
    }
    
    const url = `/glass_pricing/${fileName}`;
    console.log(`Loading glass types for category ${category} from ${url}`);
    
    const types = await fetchAndParseCSV(url, source);
    
    // Ensure the type field is set correctly based on the category
    return types.map(type => ({
      ...type,
      type: category
    }));
  } catch (error) {
    console.error(`Error loading glass types for category ${category}:`, error);
    return [];
  }
};

/**
 * Loads glass types from a specific source's whole file
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Promise resolving to an array of GlassType objects
 */
export const loadGlassTypesFromWholeFile = async (source: string): Promise<GlassType[]> => {
  try {
    const sourceLower = source.toLowerCase();
    const url = `/glass_pricing/whole/${sourceLower}.csv`;
    
    console.log(`Loading glass types from whole file: ${url}`);
    return await fetchAndParseCSV(url, source);
  } catch (error) {
    console.error(`Error loading glass types from whole file for source ${source}:`, error);
    return [];
  }
};

/**
 * Scans for available glass type groups (subdirectories)
 * @returns Promise resolving to an array of group names
 */
export const getAvailableGlassTypeGroups = async (): Promise<string[]> => {
  try {
    const directories = await scanGlassPricingDirectories();
    console.log('Available glass type groups:', directories);
    return directories;
  } catch (error) {
    console.error('Error getting available glass type groups:', error);
    return ['whole']; // Fallback to just the 'whole' directory
  }
};

/**
 * Loads glass types from a specific group (subdirectory)
 * @param group Group name (subdirectory)
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Promise resolving to an array of GlassType objects
 */
export const loadGlassTypesByGroup = async (group: string, source: string): Promise<GlassType[]> => {
  try {
    console.log(`Loading glass types from group ${group} for source ${source}`);
    return await loadGlassTypesFromDirectory(group, source);
  } catch (error) {
    console.error(`Error loading glass types from group ${group} for source ${source}:`, error);
    return [];
  }
};


/**
 * Fallback glass types for when loading from CSV fails
 */
export const getFallbackGlassTypes = (source: string): GlassType[] => {
  const fallbackTypes: GlassType[] = [
    {
      id: uuidv4(),
      name: 'Clear Mirror',
      type: 'Mirror',
      thickness: 0.25,
      thicknessStr: '1/4',
      lbsPerSqFt: 3.24,
      price: 7.28,
      source
    },
    {
      id: uuidv4(),
      name: 'Clear',
      type: 'Monolithic',
      thickness: 0.25,
      thicknessStr: '1/4',
      lbsPerSqFt: 3.24,
      price: 3.25,
      source
    },
    {
      id: uuidv4(),
      name: 'Clear over Clear',
      type: 'IG Commercial',
      thickness: 1,
      thicknessStr: '1',
      lbsPerSqFt: 6.5,
      price: 11.63,
      source
    }
  ];
  
  return fallbackTypes;
};
